import React, { ImgHTMLAttributes } from 'react';
import {
  ContentAudience,
  ContentType,
  ContentWorkflow,
  WorkflowState,
  WorkflowSteps,
  PastTenseCurrentStateLabel,
  WorkflowStepsCurrentStateLabel,
  isRejectedPublisherReview,
} from '@amzn/awscat-pattern-library-service-model';
import { useState, useEffect, useMemo, useCallback } from 'react';
import PortalServiceClient from '../../api/PortalServiceClient';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import {
  getCurrentContentState,
  PastTenseToolTipLabel,
  Prefix,
  Svg,
  WorkflowStepsToolTipLabel,
} from '../../api/WorkflowProgressUtil';
import { Tooltip } from '@amzn/awsui-components-core';

const ProgressDiagramItemImage = ({
  svg,
  ...rest
}: { svg: Svg } & ImgHTMLAttributes<HTMLImageElement>) => {
  const rootPath = '/images/progress-diagram/';

  switch (svg) {
    case Svg.Completed:
      return <img {...rest} src={`${rootPath}progress-completed.svg`} alt="completed" />;
    case Svg.Current:
      return <img {...rest} src={`${rootPath}progress-current.svg`} alt="current" />;
    case Svg.Final:
      return <img {...rest} src={`${rootPath}progress-final.svg`} alt="final" />;
    case Svg.Rejected:
      return <img {...rest} src={`${rootPath}progress-rejected.svg`} alt="rejected" />;
    case Svg.Upcoming:
      return <img {...rest} src={`${rootPath}progress-upcoming.svg`} alt="upcoming" />;
  }
  // @ts-expect-error TS7027 - strict migration
  return null;
};

const ProgressDiagramItem = ({ svg, label }: { svg: Svg; label: string }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [tooltipVisible, setTooltipVisible] = React.useState(false);

  return (
    <div
      ref={ref}
      onPointerEnter={() => setTooltipVisible(true)}
      onPointerLeave={() => setTooltipVisible(false)}
      onFocus={() => setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
      className="progress-bar-tooltip"
    >
      {<ProgressDiagramItemImage svg={svg} />}
      {tooltipVisible && (
        <Tooltip
          content={label}
          getTrack={() => ref.current}
          onEscape={() => setTooltipVisible(false)}
          position="top"
        />
      )}
    </div>
  );
};

interface ProgressDiagramProps {
  workflow: ContentWorkflow;
}

const ProgressDiagram = ({ workflow }: ProgressDiagramProps) => {
  const portalServiceClient: PortalServiceClient = useMemo(() => PortalServiceClient.create(), []);
  const flashbarMessageBroker: FlashbarMessageBroker = useMemo(
    () => FlashbarMessageBroker.create(),
    [],
  );

  const [currentStateLabel, setCurrentStateLabel] = useState<string>('');
  const [stepList, setStepList] = useState<Array<[Svg, string]>>([]);

  const generateSteps = useCallback(() => {
    const { contentAudience, draftContentMetadata, techWriterId } = workflow;
    const { contentType } = draftContentMetadata;
    const approvalWorkflowSteps = [];

    approvalWorkflowSteps.push([
      WorkflowSteps.Draft,
      [
        WorkflowState.NEW,
        WorkflowState.MODIFIED,
        WorkflowState.REJECTED,
        WorkflowState.UNPUBLISHED,
      ],
    ]);

    /* What's new - Post content type */
    if (contentType === ContentType.Post) {
      approvalWorkflowSteps.push(
        contentAudience === ContentAudience.INTERNAL
          ? [WorkflowSteps.Internal, [WorkflowState.PUBLISHED]]
          : [WorkflowSteps.External, [WorkflowState.FINALIZED, WorkflowState.REVOKED]],
      );
      return approvalWorkflowSteps;
    }

    // Quality check replaced peer review; only content still in legacy peer review shows that step
    const peerReviewStates = [WorkflowState.IN_REVIEW, WorkflowState.PENDING_REVIEW];
    approvalWorkflowSteps.push(
      peerReviewStates.includes(getCurrentContentState(workflow))
        ? [WorkflowSteps.Peer, peerReviewStates]
        : [WorkflowSteps.QualityCheck, [WorkflowState.QUALITY_CHECK]],
    );

    approvalWorkflowSteps.push([
      WorkflowSteps.Publisher,
      [WorkflowState.REVIEWED, WorkflowState.PUBLISHER_REVIEW],
    ]);

    // Internal published step
    approvalWorkflowSteps.push([WorkflowSteps.Internal, [WorkflowState.PUBLISHED]]);

    // External published step
    if (
      contentAudience !== ContentAudience.INTERNAL &&
      contentAudience !== ContentAudience.EXTERNAL &&
      contentAudience !== ContentAudience.PARTNER
    ) {
      if (techWriterId) {
        // with tech writer assigned
        approvalWorkflowSteps.push(
          [WorkflowSteps.TechwriterPool, []],
          [WorkflowSteps.TechwriterReview, [WorkflowState.PUBLISHED, WorkflowState.FINALIZING]],
          [WorkflowSteps.External, [WorkflowState.FINALIZED, WorkflowState.REVOKED]],
        );
      } else {
        approvalWorkflowSteps.push(
          [WorkflowSteps.TechwriterPool, [WorkflowState.PUBLISHED]],
          [WorkflowSteps.TechwriterReview, [WorkflowState.FINALIZING]],
          [WorkflowSteps.External, [WorkflowState.FINALIZED, WorkflowState.REVOKED]],
        );
      }
    }

    // Public distribution step
    if (contentAudience === ContentAudience.PUBLIC) {
      approvalWorkflowSteps.push([WorkflowSteps.Public, [WorkflowState.DISTRIBUTED]]);
    }

    return approvalWorkflowSteps;
  }, [workflow]);

  const createCurrentStateLabel = useCallback(
    async (
      approvalWorkflowSteps: Array<[WorkflowSteps, [WorkflowState]]>,
      currentState: WorkflowState,
    ) => {
      const { contentAudience, techWriterId } = workflow;
      let workflowStep: WorkflowSteps;
      approvalWorkflowSteps.forEach((item) => {
        const wfstep = item[0];
        const wfstates = item[1];

        // published internally but not marked for internal
        const notMarkedForInternal =
          currentState === WorkflowState.PUBLISHED &&
          wfstep === WorkflowSteps.Internal &&
          contentAudience !== ContentAudience.INTERNAL &&
          contentAudience !== ContentAudience.EXTERNAL &&
          contentAudience !== ContentAudience.PARTNER;

        if (wfstates.includes(currentState) && !notMarkedForInternal) {
          workflowStep = wfstep;
        }
      });

      let newCurrentStateLabel: string | undefined;
      if (currentState === WorkflowState.REJECTED) {
        newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(WorkflowSteps.Rejected);
      } else if (isRejectedPublisherReview(workflow)) {
        newCurrentStateLabel = `${WorkflowStepsCurrentStateLabel.get(WorkflowSteps.Publisher)}: Rejected`;
      } else if (currentState === WorkflowState.REVOKED) {
        newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(WorkflowSteps.Revoked);
      } else if (currentState === WorkflowState.UNPUBLISHED) {
        newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(WorkflowSteps.Unpublished);
      } else if (
        [WorkflowSteps.Internal, WorkflowSteps.External, WorkflowSteps.Public].includes(
          // @ts-expect-error TS2454 - strict migration
          workflowStep,
        )
      ) {
        // @ts-expect-error TS2454 - strict migration
        newCurrentStateLabel = PastTenseCurrentStateLabel.get(workflowStep);
      } else if (
        // @ts-expect-error TS2454 - strict migration
        [WorkflowSteps.TechwriterPool].includes(workflowStep) &&
        workflow.skipDraftReview
      ) {
        newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(WorkflowSteps.SendBackToAuthor);
        // @ts-expect-error TS2454 - strict migration
      } else if (techWriterId && workflowStep === WorkflowSteps.TechwriterReview) {
        try {
          const techWriters = await portalServiceClient.getUserProfiles([techWriterId]);
          const techWriterProfile = techWriters?.[0];
          const techWriterName = techWriterProfile?.name;

          if (workflow.skipDraftReview) {
            newCurrentStateLabel = `${WorkflowStepsCurrentStateLabel.get(WorkflowSteps.SendBackToAuthor)} (${techWriterName} assigned)`;
          } else {
            newCurrentStateLabel = `${WorkflowStepsCurrentStateLabel.get(workflowStep)} (${techWriterName} assigned)`;
          }
        } catch (e) {
          newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(workflowStep);
          flashbarMessageBroker.pushWarnMessage('Unable to fetch tech writer profile');
        }
      } else {
        // @ts-expect-error TS2454 - strict migration
        newCurrentStateLabel = WorkflowStepsCurrentStateLabel.get(workflowStep);
      }
      // @ts-expect-error TS2345 - strict migration
      setCurrentStateLabel(newCurrentStateLabel);
    },
    [workflow, portalServiceClient, flashbarMessageBroker],
  );

  const getSvg = useCallback(
    (
      prefix: Prefix,
      workflowStep: WorkflowSteps,
      currentState: WorkflowState,
      wfstates: WorkflowState[],
    ) => {
      const { contentAudience } = workflow;
      let svg: Svg;

      if (wfstates.includes(currentState)) {
        if (
          [WorkflowState.REJECTED, WorkflowState.UNPUBLISHED, WorkflowState.REVOKED].includes(
            currentState,
          ) ||
          isRejectedPublisherReview(workflow)
        ) {
          svg = Svg.Rejected;
        } else if (
          // if published internally but not mark for internal
          currentState === WorkflowState.PUBLISHED &&
          workflowStep === WorkflowSteps.Internal &&
          contentAudience !== ContentAudience.INTERNAL &&
          contentAudience !== ContentAudience.EXTERNAL &&
          contentAudience !== ContentAudience.PARTNER
        ) {
          svg = Svg.Completed;
        } else {
          svg = Svg.Current;
        }
      } else {
        if (prefix === Prefix.Completed) {
          svg = Svg.Completed;
        } else if (prefix === Prefix.Next) {
          svg = Svg.Upcoming;
        } else if (prefix === Prefix.Upcoming) {
          svg = Svg.Upcoming;
        } else if (prefix === Prefix.Final) {
          svg = Svg.Final;
        }
      }

      // @ts-expect-error TS2454 - strict migration
      return svg;
    },
    [workflow],
  );

  const updatePrefix = (
    currentState: WorkflowState,
    prefix: Prefix,
    svg: Svg,
    index: number,
    approvalWorkflowSteps: Array<[WorkflowSteps, [WorkflowState]]>,
  ) => {
    let upcomingPrefix: Prefix;
    // @ts-expect-error TS2532 - strict migration
    const wfstates: WorkflowState[] = approvalWorkflowSteps[index][1];
    if (wfstates.includes(currentState) && svg === Svg.Current) {
      prefix = prefix === Prefix.Final ? Prefix.Completed : Prefix.Currently;
    }

    if (index === approvalWorkflowSteps.length - 2) {
      upcomingPrefix = Prefix.Final;
    } else if (wfstates.includes(currentState) && svg !== Svg.Completed) {
      upcomingPrefix = Prefix.Next;
    } else if (prefix === Prefix.Next) {
      upcomingPrefix = Prefix.Upcoming;
    }

    // @ts-expect-error TS2454 - strict migration
    return [prefix, upcomingPrefix];
  };

  const generateToolTipLabel = useCallback(
    (
      prefix: Prefix,
      workflowStep: WorkflowSteps,
      wfstates: WorkflowState[],
      currentState: WorkflowState,
    ) => {
      let generatedLabel: string;
      if (prefix === Prefix.Final && wfstates.includes(currentState)) {
        // @ts-expect-error TS2322 - strict migration
        generatedLabel = PastTenseToolTipLabel.get(workflowStep);
      } else if (
        wfstates.includes(currentState) &&
        (WorkflowState.REJECTED === currentState || isRejectedPublisherReview(workflow))
      ) {
        // @ts-expect-error TS2322 - strict migration
        generatedLabel = WorkflowStepsToolTipLabel.get(WorkflowSteps.Rejected);
      } else if (wfstates.includes(currentState) && WorkflowState.UNPUBLISHED === currentState) {
        // @ts-expect-error TS2322 - strict migration
        generatedLabel = WorkflowStepsToolTipLabel.get(WorkflowSteps.Unpublished);
      } else if (wfstates.includes(currentState) && WorkflowState.REVOKED === currentState) {
        // @ts-expect-error TS2322 - strict migration
        generatedLabel = WorkflowStepsToolTipLabel.get(WorkflowSteps.Revoked);
      } else if (
        [WorkflowSteps.Internal, WorkflowSteps.External, WorkflowSteps.Public].includes(
          workflowStep,
        ) &&
        [Prefix.Completed, Prefix.Currently].includes(prefix)
      ) {
        // @ts-expect-error TS2322 - strict migration
        generatedLabel = PastTenseToolTipLabel.get(workflowStep);
      } else if (
        [WorkflowSteps.TechwriterPool, WorkflowSteps.TechwriterReview].includes(workflowStep) &&
        workflow.skipDraftReview
      ) {
        generatedLabel = 'Sent back to author.';
      } else {
        generatedLabel = `${prefix} ${WorkflowStepsToolTipLabel.get(workflowStep)}`;
      }
      return generatedLabel;
    },
    [workflow],
  );

  const generateStepList = useCallback(
    (
      approvalWorkflowSteps: Array<[WorkflowSteps, [WorkflowState]]>,
      currentState: WorkflowState,
    ) => {
      let prefix = [WorkflowState.NEW, WorkflowState.MODIFIED].includes(currentState)
        ? Prefix.Currently
        : Prefix.Completed;

      const newStepList: Array<[Svg, string]> = [];

      approvalWorkflowSteps.forEach((item, index) => {
        const workflowStep = item[0];
        const wfstates = item[1];

        let svg: Svg;
        svg = getSvg(prefix, workflowStep, currentState, wfstates);

        let upcomingPrefix: Prefix;
        // @ts-expect-error TS2322 - strict migration
        [prefix, upcomingPrefix] = updatePrefix(
          currentState,
          prefix,
          svg,
          index,
          approvalWorkflowSteps,
        );

        const label = generateToolTipLabel(prefix, workflowStep, wfstates, currentState);

        newStepList.push([svg, label]);

        prefix = upcomingPrefix ? upcomingPrefix : prefix;
      });
      setStepList(newStepList);
    },
    [generateToolTipLabel, getSvg],
  );

  const generateDiagram = () => {
    return stepList.map(([svg, label]) => {
      return <ProgressDiagramItem key={label} label={label} svg={svg}></ProgressDiagramItem>;
    });
  };

  useEffect(() => {
    const setUp = async () => {
      const currentState = getCurrentContentState(workflow);
      const approvalWorkflowSteps = generateSteps();
      // @ts-expect-error TS2345 - strict migration
      await createCurrentStateLabel(approvalWorkflowSteps, currentState);
      // @ts-expect-error TS2345 - strict migration
      generateStepList(approvalWorkflowSteps, currentState);
    };
    setUp();
  }, [
    workflow.draftState,
    workflow.publicationState,
    workflow.contentAudience,
    workflow.draftContentMetadata,
    workflow.techWriterId,
    createCurrentStateLabel,
    generateStepList,
    generateSteps,
    workflow,
  ]);

  const progressDiagram = generateDiagram();

  if (!progressDiagram) {
    return null;
  }
  return (
    <div>
      <div className="progress-diagram">{progressDiagram}</div>
      <span className="progress-diagram-current-state">{currentStateLabel}</span>
    </div>
  );
};

export default ProgressDiagram;
